/**
 * useNotificationSocket — Unified WebSocket hook for real-time notifications.
 *
 * Replaces `useCallNotifications` and extends it to:
 * 1. Handle all notification types (not just call.invite)
 * 2. Push received notifications into useNotificationStore
 * 3. Trigger native desktop/browser notifications
 * 4. Support reconnect with exponential backoff
 * 5. Send keepalive pings every 30s
 * 6. Tell the server when the user is active here (typing, clicking, scrolling in a visible
 *    tab; at most once a minute), so direct messages go to their phone once they step away
 *
 * Mount once in App when the user is authenticated.
 */
import { useEffect, useRef, useCallback } from 'react';
import { useAuthStore, useNotificationStore, useIncomingCallStore } from '../stores';
import type { IncomingCall } from '../stores';
import { showCallNotification, nativeNotify } from '../lib/tauriBridge';
import { shouldInterrupt, useFocusStore } from '../stores/focusStore';
import { api } from '../lib/api';
import { jittered } from '../lib/backoff';
import i18next from 'i18next';

/** At most one activity signal per this interval (the server's idle limit is 3 minutes). */
export const ACTIVITY_THROTTLE_MS = 60_000;
const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart'] as const;

function getNotificationWsUrl(): string {
  const raw = import.meta.env.VITE_NOTIFICATION_WS_URL as string | undefined;
  if (raw && raw.startsWith('ws')) return raw;

  const apiBase = (import.meta.env.VITE_API_URL as string | undefined) ?? '/api';
  if (apiBase.startsWith('http')) {
    return apiBase.replace('https://', 'wss://').replace('http://', 'ws://') + '/v1/ws/notifications';
  }
  const p = location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${p}//${location.host}${apiBase}/v1/ws/notifications`;
}

export function useNotificationSocket() {
  const { user, isAuthenticated } = useAuthStore();
  const { addNotification } = useNotificationStore();
  const { setIncomingCall } = useIncomingCallStore();
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const attemptRef = useRef(0);
  const shouldReconnectRef = useRef(true);

  const connect = useCallback(async () => {
    if (!isAuthenticated || !user) return;

    // A token that is still valid: after a drop (network, server restart) the stored one may
    // have expired, and the server refuses the reconnect with 401.
    const token = await api.freshToken();
    if (!token || !shouldReconnectRef.current) return;

    // lang: the server renders notification titles in the language this app shows
    const url = `${getNotificationWsUrl()}?token=${encodeURIComponent(token)}&lang=${encodeURIComponent(i18next.language || 'en')}`;

    try {
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        attemptRef.current = 0;

        // Start keepalive ping every 30s
        pingTimerRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }));
          }
        }, 30000);
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data as string) as {
            type: string;
            data?: Record<string, unknown>;
            payload?: IncomingCall;
          };

          switch (msg.type) {
            case 'connected':
              // Connection confirmed — no action needed
              break;

            case 'pong':
              // Keepalive response — no action needed
              break;

            case 'call.invite':
              // Legacy call invite format (from useCallNotifications)
              if (msg.payload) {
                setIncomingCall(msg.payload);
                void showCallNotification(msg.payload.callerName, msg.payload.callType);
              }
              break;

            case 'notification': {
              // General notification from the service
              const data = msg.data;
              if (data && typeof data === 'object') {
                // Handle call-type notifications (from Kafka consumer)
                if (data.type === 'call' && data.data) {
                  const callData = data.data as Record<string, unknown>;
                  // The title is translated ("Anna ringer"), so the name comes from the data
                  const callerName = (callData.callerName as string | undefined)
                    ?? ((data.title as string) ?? '').replace(' is calling', '');
                  setIncomingCall({
                    callId: (callData.callId ?? '') as string,
                    callerId: (callData.callerId ?? '') as string,
                    callerName,
                    roomId: (callData.roomId ?? '') as string,
                    callType: (callData.callType ?? 'audio') as 'audio' | 'video',
                  });
                  void showCallNotification(
                    callerName,
                    (callData.callType ?? 'audio') as string,
                  );
                }

                // Push to notification store
                addNotification({
                  id: (data.id as string) ?? crypto.randomUUID(),
                  title: (data.title as string) ?? '',
                  body: (data.body as string) ?? '',
                  type: (data.type as string) ?? 'message',
                  targetId: data.data
                    ? (((data.data as Record<string, unknown>).roomId ??
                        (data.data as Record<string, unknown>).fileId) as string | undefined)
                    : undefined,
                  messageId: data.data
                    ? ((data.data as Record<string, unknown>).messageId as string | undefined)
                    : undefined,
                  read: false,
                  createdAt: (data.created_at as string) ?? new Date().toISOString(),
                });

                // Fire native/browser notification for non-call types, unless Focus is holding it
                if (data.type !== 'call' && !shouldInterrupt(data.type as string | undefined)) {
                  useFocusStore.getState().noteHeld();
                } else if (data.type !== 'call') {
                  void nativeNotify(
                    (data.title as string) ?? 'Eurion',
                    (data.body as string) ?? '',
                  );
                }
              }
              break;
            }

            default:
              // Unknown message type — ignore
              break;
          }
        } catch {
          // Malformed message — ignore
        }
      };

      ws.onclose = () => {
        if (pingTimerRef.current) {
          clearInterval(pingTimerRef.current);
          pingTimerRef.current = null;
        }

        // Reconnect with exponential backoff (max 30s). No attempt limit: giving up meant no
        // incoming-call or notification pop-ups until the page was reloaded.
        if (shouldReconnectRef.current) {
          const delay = jittered(Math.min(1000 * Math.pow(2, attemptRef.current), 30000));
          attemptRef.current++;
          reconnectTimerRef.current = setTimeout(() => {
            void connect();
          }, delay);
        }
      };

      ws.onerror = () => {
        // Will trigger onclose which handles reconnection
      };
    } catch {
      // WebSocket constructor can throw — schedule reconnect
      if (shouldReconnectRef.current) {
        const delay = jittered(Math.min(1000 * Math.pow(2, attemptRef.current), 30000));
        attemptRef.current++;
        reconnectTimerRef.current = setTimeout(() => { void connect(); }, delay);
      }
    }
  }, [isAuthenticated, user, addNotification, setIncomingCall]);

  useEffect(() => {
    shouldReconnectRef.current = true;
    void connect();

    // Back online or back to the tab: reconnect now instead of waiting out the backoff
    const reconnectNow = () => {
      const ws = wsRef.current;
      if (document.visibilityState !== 'visible' || (ws && ws.readyState <= WebSocket.OPEN)) return;
      if (reconnectTimerRef.current) { clearTimeout(reconnectTimerRef.current); reconnectTimerRef.current = null; }
      attemptRef.current = 0;
      void connect();
    };
    window.addEventListener('online', reconnectNow);
    document.addEventListener('visibilitychange', reconnectNow);

    // Language switched in Settings: new notifications arrive in it
    const sendLang = (lng: string) => {
      const ws = wsRef.current;
      if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'lang', lang: lng }));
    };
    i18next.on('languageChanged', sendLang);

    // The user is here: let the server hold back phone pushes for direct messages
    let lastActivitySent = 0;
    const sendActivity = () => {
      const ws = wsRef.current;
      const now = Date.now();
      if (document.visibilityState !== 'visible' || !ws || ws.readyState !== WebSocket.OPEN) return;
      if (now - lastActivitySent < ACTIVITY_THROTTLE_MS) return;
      lastActivitySent = now;
      ws.send(JSON.stringify({ type: 'activity' }));
    };
    for (const ev of ACTIVITY_EVENTS) window.addEventListener(ev, sendActivity, { passive: true });
    document.addEventListener('visibilitychange', sendActivity);

    return () => {
      i18next.off('languageChanged', sendLang);
      for (const ev of ACTIVITY_EVENTS) window.removeEventListener(ev, sendActivity);
      document.removeEventListener('visibilitychange', sendActivity);
      window.removeEventListener('online', reconnectNow);
      document.removeEventListener('visibilitychange', reconnectNow);
      shouldReconnectRef.current = false;
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      if (pingTimerRef.current) {
        clearInterval(pingTimerRef.current);
        pingTimerRef.current = null;
      }
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [connect]);
}
